import { Box, Card, EmptyState, Spinner } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { Batch } from '@common/interfaces/api/batchesApi';
import type { DeclarationWithActivities } from '@common/interfaces/api/declarationsApi';
import type { CategorizedFinancialItems } from '@frontend/components/pages/processes/adminProcessFinancials/utils/categorizeItems';
import { type FundingResult, isFundingError, isNotFunded } from '@common/interfaces/api/fundingResultsApi';
import { isNotNull } from '@common/utils/isNotNull';
import { FinanceDeclarations } from '@frontend/components/pages/processes/adminProcessFinancials/FinanceDeclaration';
import { FundingErrorDetails } from '@frontend/components/pages/processes/adminProcessFinancials/FundingErrorDetails';
import { categorizeFinancialItems } from '@frontend/components/pages/processes/adminProcessFinancials/utils/categorizeItems';
import { findFundingResultByProcessNumber } from '@frontend/services/api/fundingResultsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type {
    CompositePatientInvoice,
    FundingExclusion,
    InvoiceRecord,
    SingularPatientInvoice,
} from '../adminProcessDetailsTypes';
import { CompositePatientInvoices } from './CompositePatientInvoices';
import { InvoiceRecords } from './InvoiceRecords';
import { NotFundedReason } from './NotFundedReason';
import { SingularPatientInvoices } from './SingularPatientInvoices';

type AdminProcessDetailsFinancialStatusProps = {
    fundingExclusion?: FundingExclusion;
    hasCard?: boolean;
    invoiceRecords: InvoiceRecord[];
    processNumber: number;
};

function createInitialFinanceItem<T>(): CategorizedFinancialItems<T> {
    return {
        completed: [],
        pending: [],
    };
}

type DeclarationItems = {
    compositePatientInvoiceItems: CategorizedFinancialItems<CompositePatientInvoice>;
    declarationItems: CategorizedFinancialItems<DeclarationWithActivitiesAndBatch>;
    invoiceItems: CategorizedFinancialItems<InvoiceRecord>;
    singularPatientInvoiceItems: CategorizedFinancialItems<SingularPatientInvoice>;
};

type DeclarationWithActivitiesAndBatch = DeclarationWithActivities & { exportBatch?: Batch };

export function FinancialStatus({
    hasCard,
    fundingExclusion,
    invoiceRecords,
    processNumber,
}: AdminProcessDetailsFinancialStatusProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();
    const abortSignal = useAbortSignal();
    const [fundingResult, setFundingResult] = useState<FundingResult>();
    const [financeDeclarations, setFinanceDeclarations] = useState<DeclarationWithActivitiesAndBatch[]>([]);
    const [financeCompositePatientInvoices, setFinanceCompositePatientInvoices] = useState<CompositePatientInvoice[]>(
        []
    );
    const [financeSingularPatientInvoices, setFinanceSingularPatientInvoices] = useState<SingularPatientInvoice[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const [
        { declarationItems, compositePatientInvoiceItems, invoiceItems, singularPatientInvoiceItems },
        setCategorizedDeclarationItems,
    ] = useState<DeclarationItems>({
        compositePatientInvoiceItems: createInitialFinanceItem<CompositePatientInvoice>(),
        declarationItems: createInitialFinanceItem<DeclarationWithActivitiesAndBatch>(),
        invoiceItems: createInitialFinanceItem<InvoiceRecord>(),
        singularPatientInvoiceItems: createInitialFinanceItem<SingularPatientInvoice>(),
    });

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            await Promise.all([
                loadFinanceDeclarations(),
                loadFinanceCompositePatientInvoices(),
                loadFundingResult(),
                loadSingularPatientInvoiceRecords(),
            ]);
            setIsLoading(false);
        };

        load();
    }, [processNumber]);

    useEffect(() => {
        setCategorizedDeclarationItems({
            compositePatientInvoiceItems: categorizeFinancialItems(
                financeCompositePatientInvoices,
                (record) => !record.runNumber
            ),
            declarationItems: categorizeFinancialItems(
                financeDeclarations,
                (declaration) => !declaration.exportBatch?.runNumber
            ),
            invoiceItems: categorizeFinancialItems(invoiceRecords, (record) => !record.runNumber),
            singularPatientInvoiceItems: categorizeFinancialItems(
                financeSingularPatientInvoices,
                (record) => !record.batchId
            ),
        });
    }, [isLoading]);

    const hasPendingOrCompletedItems = (
        items: CategorizedFinancialItems<
            CompositePatientInvoice | DeclarationWithActivitiesAndBatch | InvoiceRecord | SingularPatientInvoice
        >
    ) => {
        return Boolean(items.pending.length > 0 || items.completed.length > 0);
    };

    const hasChildren = [
        hasPendingOrCompletedItems(declarationItems),
        hasPendingOrCompletedItems(invoiceItems),
        hasPendingOrCompletedItems(compositePatientInvoiceItems),
        hasPendingOrCompletedItems(singularPatientInvoiceItems),
        fundingExclusion,
        fundingResult,
    ].some(Boolean);

    const financialStatusComponents = (
        <>
            {isFundingError(fundingResult) && <FundingErrorDetails fundingError={fundingResult} />}

            {isNotFunded(fundingResult) && (
                <NotFundedReason
                    fundingExclusion={fundingExclusion}
                    notFunded={fundingResult}
                />
            )}

            <FinanceDeclarations
                declarationsRecords={declarationItems?.pending}
                label={translate.finance.toBeDeclared}
            />

            <FinanceDeclarations
                declarationsRecords={declarationItems?.completed}
                label={translate.finance.declared}
            />

            <InvoiceRecords
                invoiceRecords={invoiceItems?.pending}
                label={translate.finance.toBeInvoiced}
            />

            <InvoiceRecords
                invoiceRecords={invoiceItems?.completed}
                label={translate.finance.invoiced}
            />

            <CompositePatientInvoices
                compositePatientInvoices={compositePatientInvoiceItems?.pending}
                label={translate.finance.toBeInvoicedPatient}
            />

            <CompositePatientInvoices
                compositePatientInvoices={compositePatientInvoiceItems?.completed}
                label={translate.finance.invoicedPatient}
            />

            <SingularPatientInvoices
                label={translate.finance.toBeInvoicedPatient}
                singularPatientInvoices={singularPatientInvoiceItems.pending}
            />

            <SingularPatientInvoices
                label={translate.finance.invoicedPatient}
                singularPatientInvoices={singularPatientInvoiceItems.completed}
            />
        </>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box>
                    <Spinner />
                </Box>
            );
        }

        if (hasChildren) {
            return hasCard ? (
                <Card>
                    <Card.Body>{financialStatusComponents}</Card.Body>
                </Card>
            ) : (
                financialStatusComponents
            );
        }

        return (
            <EmptyState
                icon="medicalfile"
                subtitle=""
                title={translate.finance.noFinancialStatus}
                variant="left-aligned"
            />
        );
    };

    return <>{renderContent()}</>;

    async function loadDeclarationRecords() {
        const declarationResult: DeclarationWithActivities[] = (
            await abortableFetch.fetchJson(`/api/finance/declarations/${processNumber}`, 'GET')
        ).json;

        return declarationResult;
    }

    async function loadFinanceCompositePatientInvoices() {
        const compositePatientInvoicesResult = await loadCompositePatientInvoices();

        const exportBatchIds = compositePatientInvoicesResult.map((invoice) => invoice.exportBatchId).filter(isNotNull);

        const batchesData = await loadBatchData(exportBatchIds);
        const compositePatientInvoicesWithBatch = compositePatientInvoicesResult.map((invoice) => {
            const exportBatch = batchesData.find((batch) => batch.id === invoice.exportBatchId);

            return { ...invoice, runDate: exportBatch?.runDate ?? null, runNumber: exportBatch?.runNumber ?? null };
        });

        setFinanceCompositePatientInvoices(compositePatientInvoicesWithBatch);
    }

    async function loadFundingResult() {
        const result = await findFundingResultByProcessNumber(processNumber, abortSignal);
        setFundingResult(result);
    }

    async function loadCompositePatientInvoices() {
        const compositePatientInvoicesResult = await abortableFetch.fetchJson(
            `/api/finance/compositePatientInvoices/${processNumber}`,
            'GET'
        );

        return compositePatientInvoicesResult.json as CompositePatientInvoice[];
    }

    async function loadFinanceDeclarations() {
        const declarationsData = await loadDeclarationRecords();

        const exportBatchIds = declarationsData.map((declaration) => declaration.exportBatchId).filter(isNotNull);

        const batchesData = await loadBatchData(exportBatchIds);
        const declarationWithBatch = declarationsData.map((declaration) => {
            const exportBatch = batchesData.find((batch) => batch.id === declaration.exportBatchId);

            return { ...declaration, exportBatch };
        });

        setFinanceDeclarations(declarationWithBatch);
    }

    async function loadBatchData(exportBatchIds: string[]) {
        const result: Batch[] = (await abortableFetch.postJson(`/api/batches/selected`, exportBatchIds)).json.items;

        return result;
    }

    async function loadSingularPatientInvoiceRecords() {
        const response = await abortableFetch.fetchJson(`/api/singularPatientInvoiceRecords/${processNumber}`, 'GET');

        const invoices = (await response.json) as SingularPatientInvoice[];

        const exportBatchIds = invoices.map((invoice) => invoice.batchId).filter(isNotNull);

        const batchesData = await loadBatchData(exportBatchIds);
        const invoicesWithBatch = invoices.map((invoice) => {
            const exportBatch = batchesData.find((batch) => batch.id === invoice.batchId);

            return { ...invoice, runDate: exportBatch?.runDate ?? null, runNumber: exportBatch?.runNumber ?? null };
        });

        setFinanceSingularPatientInvoices(invoicesWithBatch);
    }
}
